import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Wand2, Save, X, EyeOff, Upload, BookOpen, LayoutGrid, Heart, ImageOff } from 'lucide-react';
import { SpecialistIcon } from '../chatHome/specialistIcons';
import {
  adminListCatalog, adminSaveAgent, adminDeleteAgent, adminGenerateAgentAvatar, adminSetAgentAvatar,
  adminListGallery, adminGenerateGallery, type CatalogAgent, type GalleryPostRow,
} from '../../services/adminService';
import { getAgentBio, type AgentBio } from '../../services/agentService';
import AgentVoicePicker from './AgentVoicePicker';

const GALLERY_STYLES = ['cyber', 'cute', 'kawaii', 'gothic', 'kpop', 'mythology', 'corporate', 'vampire', 'knight', 'stellar', 'couture', 'cyberwuxia', 'wuxia', 'cyborg', 'darkfantasy'];

/**
 * One gallery thumbnail with a robust broken-image fallback. Three real states an admin needs to tell
 * apart: the image loads (happy path); the post has NO stored image_path (shows the scene as text — the
 * generation never produced a file); or the image_path is set but the URL 404s / fails to load (shows an
 * ImageOff icon + scene, so a broken bucket/path reads as BROKEN, not as an empty slot).
 */
const GalleryThumb: React.FC<{ post: GalleryPostRow }> = ({ post }) => {
  const [failed, setFailed] = useState(false);
  const showImage = !!post.image_path && !failed;
  return (
    <div className="relative rounded-md overflow-hidden bg-secondary-100 dark:bg-secondary-800 aspect-[4/5]">
      {showImage ? (
        <img src={post.image_path as string} alt={post.scene} className="w-full h-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span className="flex flex-col items-center justify-center gap-1 w-full h-full text-[10px] text-secondary-400 px-1 text-center">
          {failed && <ImageOff className="w-4 h-4" />}
          {post.scene}
        </span>
      )}
      {typeof post.like_count === 'number' && post.like_count > 0 && (
        <span className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-[9px] px-1 py-0.5 flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" /> {post.like_count.toLocaleString()}</span>
      )}
    </div>
  );
};

type Draft = Partial<CatalogAgent> & { id: string; is_new?: boolean };
const TIERS = ['CASUAL', 'SMART', 'EXPERT', 'RESEARCH', 'CODE'];
const input = 'w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm focus:outline-none focus:border-secondary-500';

/** Admin no-code manager for the default-agent roster: edit/add/disable + generate avatars. */
const CatalogAdminPanel: React.FC = () => {
  const [agents, setAgents] = useState<CatalogAgent[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<'' | 'save' | 'avatar' | 'del' | 'gallery'>('');
  const [error, setError] = useState('');
  const [avatarPrompt, setAvatarPrompt] = useState('');
  // Social-media gallery for the agent being edited (admin on-demand generation).
  const [gallery, setGallery] = useState<GalleryPostRow[] | null>(null);
  const [galStyle, setGalStyle] = useState('cyber');
  const [galCount, setGalCount] = useState(4);
  const [galNote, setGalNote] = useState('');
  // The agent's reference corpus (static knowledge pack) — read-only, admin-only inspection. This is
  // the internal detail hidden from end users in the chat Agent-details panel; admins see it here.
  const [bio, setBio] = useState<AgentBio | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => adminListCatalog().then(setAgents).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const set = (k: keyof CatalogAgent, v: any) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  // Update a nested identity field (personality/gender/…) while preserving the rest of the persona card.
  const setIdentity = (k: string, v: any) => setDraft((d) => (d ? { ...d, identity: { ...(d.identity || {}), [k]: v } } : d));
  const startNew = () => { setError(''); setAvatarPrompt(''); setBio(null); setGallery(null); setGalNote(''); setDraft({ id: '', name: '', persona: 'Specialist', description: '', system_prompt: 'You are a helpful specialist.', model_tier: 'SMART', temperature: 0.6, is_new: true }); };
  const startEdit = (a: CatalogAgent) => {
    setError(''); setAvatarPrompt(''); setBio(null); setGallery(null); setGalNote(''); setDraft({ ...a });
    getAgentBio(a.id).then(setBio).catch(() => setBio(null)); // reference corpus (null for custom agents)
    adminListGallery(a.id).then((r) => setGallery(r.posts || [])).catch(() => setGallery([])); // existing social posts
  };

  const save = async () => {
    if (!draft) return;
    setBusy('save'); setError('');
    try { await adminSaveAgent(draft); setDraft(null); await load(); }
    catch (e: any) { setError(e.message || 'Save failed'); } finally { setBusy(''); }
  };
  const remove = async (a: CatalogAgent) => {
    setBusy('del'); setError('');
    try { await adminDeleteAgent(a.id, true); setDraft(null); await load(); }
    catch (e: any) { setError(e.message || 'Failed'); } finally { setBusy(''); }
  };
  const genAvatar = async () => {
    if (!draft?.id) { setError('Save the agent first (needs an id)'); return; }
    setBusy('avatar'); setError('');
    try { const r = await adminGenerateAgentAvatar(draft.id, avatarPrompt || draft.description || draft.persona || ''); set('avatar_image', r.avatar_image); }
    catch (e: any) { setError(e.message || 'Avatar generation failed'); } finally { setBusy(''); }
  };
  const genGallery = async () => {
    if (!draft?.id) { setError('Save the agent first (needs an id)'); return; }
    if (!draft.avatar_image) { setError('Generate an avatar first — photos are based on it'); return; }
    setBusy('gallery'); setError(''); setGalNote('');
    try {
      const r = await adminGenerateGallery(draft.id, galStyle, galCount);
      setGallery((g) => [...(g || []), ...r.created]);
      setGalNote(`Added ${r.generated}/${r.requested} ${galStyle} photos${r.generated < r.requested ? ' (some shots failed — try again)' : ''}`);
    } catch (e: any) { setError(e.message || 'Gallery generation failed'); } finally { setBusy(''); }
  };
  const uploadAvatar = async (file?: File) => {
    if (!file || !draft?.id) { if (!draft?.id) setError('Save the agent first (needs an id)'); return; }
    setBusy('avatar'); setError('');
    try {
      const dataUrl: string = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file); });
      const r = await adminSetAgentAvatar(draft.id, dataUrl); set('avatar_image', r.avatar_image);
    } catch (e: any) { setError(e.message || 'Upload failed'); } finally { setBusy(''); }
  };

  if (agents === null) return <p className="text-sm text-secondary-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading roster…</p>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Default agents <span className="text-secondary-400 text-sm">({agents.length})</span></h2>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-sm font-medium text-white dark:text-secondary-900"><Plus className="w-4 h-4" /> Add agent</button>
      </div>
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

      {draft && (
        <div className="mt-4 rounded-xl border border-secondary-300 dark:border-secondary-700 p-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{draft.is_new ? 'New agent' : `Edit ${draft.name}`}</h3>
            <button onClick={() => setDraft(null)} className="text-secondary-400 hover:text-secondary-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {draft.is_new && <label className="block"><span className="text-xs text-secondary-500">Id (kebab-case)</span><input className={`${input} mt-1`} value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} placeholder="my-agent" /></label>}
            <label className="block"><span className="text-xs text-secondary-500">Name</span><input className={`${input} mt-1`} value={draft.name || ''} onChange={(e) => set('name', e.target.value)} /></label>
            <label className="block"><span className="text-xs text-secondary-500">Persona / role</span><input className={`${input} mt-1`} value={draft.persona || ''} onChange={(e) => set('persona', e.target.value)} /></label>
            <label className="block"><span className="text-xs text-secondary-500">Model tier</span><select className={`${input} mt-1`} value={draft.model_tier} onChange={(e) => set('model_tier', e.target.value)}>{TIERS.map((t) => <option key={t}>{t}</option>)}</select></label>
            <label className="block"><span className="text-xs text-secondary-500">Temperature ({draft.temperature})</span><input type="range" min={0} max={1} step={0.1} className="w-full mt-2" value={draft.temperature} onChange={(e) => set('temperature', Number(e.target.value))} /></label>
            {draft.model_tier === 'EXPERT' && (
              <label className="block"><span className="text-xs text-secondary-500">Reasoning effort (EXPERT)</span><select className={`${input} mt-1`} value={draft.reasoning_effort || 'medium'} onChange={(e) => set('reasoning_effort', e.target.value as CatalogAgent['reasoning_effort'])}><option value="low">Low — fast</option><option value="medium">Medium</option><option value="high">High — deeper</option></select></label>
            )}
            <label className="block sm:col-span-2"><span className="text-xs text-secondary-500">Description</span><input className={`${input} mt-1`} value={draft.description || ''} onChange={(e) => set('description', e.target.value)} /></label>
            <label className="block sm:col-span-2"><span className="text-xs text-secondary-500">Backstory</span><textarea rows={2} className={`${input} mt-1 resize-none`} value={draft.backstory || ''} onChange={(e) => set('backstory', e.target.value)} /></label>
            <label className="block"><span className="text-xs text-secondary-500">Hobby</span><input className={`${input} mt-1`} value={draft.hobby || ''} onChange={(e) => set('hobby', e.target.value)} placeholder="e.g. chess, cycling" /></label>
            <label className="block"><span className="text-xs text-secondary-500">Gender (drives avatar)</span><select className={`${input} mt-1`} value={draft.identity?.gender || 'neutral'} onChange={(e) => setIdentity('gender', e.target.value)}><option value="neutral">Neutral</option><option value="female">Female</option><option value="male">Male</option></select></label>
            <label className="block sm:col-span-2"><span className="text-xs text-secondary-500">Personality (shapes the agent's voice)</span><textarea rows={2} className={`${input} mt-1 resize-none`} value={draft.identity?.personality || ''} onChange={(e) => setIdentity('personality', e.target.value)} placeholder="Measured and data-first; cites evidence before opinion." /></label>
            <label className="block sm:col-span-2"><span className="text-xs text-secondary-500">System prompt</span><textarea rows={4} className={`${input} mt-1 resize-none`} value={draft.system_prompt || ''} onChange={(e) => set('system_prompt', e.target.value)} /></label>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center overflow-hidden shrink-0">
              {draft.avatar_image ? <img src={draft.avatar_image} alt="" className="w-full h-full object-cover" /> : <SpecialistIcon name={draft.avatar_icon || 'bot'} className="w-6 h-6 text-secondary-500" />}
            </span>
            <input className={input} placeholder="Avatar look (optional) — e.g. wise silver-haired analyst" value={avatarPrompt} onChange={(e) => setAvatarPrompt(e.target.value)} />
            <button onClick={genAvatar} disabled={busy === 'avatar'} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm shrink-0">{busy === 'avatar' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate</button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadAvatar(e.target.files?.[0]); if (fileRef.current) fileRef.current.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy === 'avatar'} title="Upload a locally-generated avatar" className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm shrink-0"><Upload className="w-4 h-4" /> Upload</button>
          </div>

          {/* Voice — gender-filtered pool with play-to-preview; "Auto" clears the override to match gender. */}
          <div className="mt-3">
            <AgentVoicePicker
              gender={draft.identity?.gender}
              voiceId={draft.voice_id}
              quality={draft.voice_quality}
              onVoice={(id) => set('voice_id', id)}
              onQuality={(q) => set('voice_quality', q)}
            />
          </div>

          {/* Social-media gallery — admin on-demand generation (pick a STYLE + count, append fresh photos). */}
          {!draft.is_new && (
            <div className="mt-4 rounded-lg border border-secondary-200 dark:border-secondary-800 p-3">
              <p className="text-xs font-medium text-secondary-500 flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" /> Social gallery {gallery !== null && <span className="text-secondary-400">({gallery.length})</span>}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select className={`${input} w-auto`} value={galStyle} onChange={(e) => setGalStyle(e.target.value)}>
                  {GALLERY_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className={`${input} w-auto`} value={galCount} onChange={(e) => setGalCount(Number(e.target.value))}>
                  {[1, 2, 3, 4, 6, 8].map((n) => <option key={n} value={n}>{n} photo{n > 1 ? 's' : ''}</option>)}
                </select>
                <button onClick={genGallery} disabled={busy === 'gallery' || !draft.avatar_image} title={draft.avatar_image ? '' : 'Generate an avatar first'} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm shrink-0 disabled:opacity-50">{busy === 'gallery' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate photos</button>
                {galNote && <span className="text-xs text-secondary-500">{galNote}</span>}
              </div>
              {busy === 'gallery' && <p className="mt-2 text-[11px] text-secondary-400">Generating {galCount} reference-locked {galStyle} photos… this can take a minute.</p>}
              {gallery && gallery.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {gallery.map((p) => <GalleryThumb key={p.id} post={p} />)}
                </div>
              )}
              {gallery !== null && gallery.length === 0 && <p className="mt-2 text-[11px] text-secondary-400">No photos yet — generate the first set above.</p>}
            </div>
          )}

          {/* Reference corpus (static knowledge pack) — read-only. Hidden from end users; admins inspect it here. */}
          {bio && (bio.knowledge.length > 0 || bio.skills.length > 0) && (
            <div className="mt-4 rounded-lg border border-secondary-200 dark:border-secondary-800 p-3">
              <p className="text-xs font-medium text-secondary-500 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Knowledgebase — reference corpus ({bio.knowledge.length})</p>
              {bio.knowledge.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {bio.knowledge.map((k, i) => (
                    <li key={i} className="text-xs text-secondary-600 dark:text-secondary-300">• {k}</li>
                  ))}
                </ul>
              )}
              {bio.skills.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {bio.skills.map((s, i) => <span key={i} className="rounded-full border border-secondary-200 dark:border-secondary-700 px-2 py-0.5 text-[11px] text-secondary-500">{s}</span>)}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={busy === 'save'} className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-secondary-900 disabled:opacity-50">{busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
            {!draft.is_new && <button onClick={() => remove(draft as CatalogAgent)} disabled={busy === 'del'} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm text-secondary-600"><EyeOff className="w-4 h-4" /> Hide from roster</button>}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {agents.map((a) => (
          <button key={a.id} onClick={() => startEdit(a)} className="flex items-center gap-3 rounded-lg border border-secondary-200 dark:border-secondary-800 p-3 text-left hover:border-secondary-400">
            <span className="w-9 h-9 rounded-full bg-secondary-100 dark:bg-secondary-900 flex items-center justify-center overflow-hidden shrink-0">
              {a.avatar_image ? <img src={a.avatar_image} alt="" className="w-full h-full object-cover" /> : <SpecialistIcon name={a.avatar_icon} className="w-5 h-5 text-secondary-500" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium truncate">{a.name} {!a.is_code && <span className="text-[10px] text-secondary-400">(custom)</span>}</span>
              <span className="block text-xs text-secondary-500 truncate">{a.persona}</span>
            </span>
            {/* Gender badge — surfaced on the tile so a wrong assignment (drives avatar/voice) is easy to spot. */}
            {(() => {
              const g = a.identity?.gender;
              const label = g === 'male' ? 'Male' : g === 'female' ? 'Female' : 'Neutral';
              const strong = g === 'male' || g === 'female';
              return (
                <span title="Presented gender (drives avatar/voice) — check for a wrong assignment"
                  className={`ml-auto shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${strong
                    ? 'border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-200'
                    : 'border-secondary-200 dark:border-secondary-800 text-secondary-400'}`}>
                  {label}
                </span>
              );
            })()}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CatalogAdminPanel;
